import { EApiRoutes } from 'api/api.utils';
import { Currency, OrderDetails, Project } from 'common/contracts';
import {
  EAwardFlowStatus,
  EOrderStatus,
  SortingDirection,
  SortingOrderValue
} from 'constants/enums';
import { statusDictionary } from 'constants/orderStatusMappings';
import Decimal from 'decimal.js';
import { UserDetails } from 'pages/Login/Login.types';
import { getSelectProjectOptions } from 'utils/projectsUtil';

import { ordersUtils } from '../utils';

import { OrderFilters, StatusOption } from './types';
import { PaymentMethod } from 'components/Order/types';
import { SortingState } from '@tanstack/react-table';

export type OrderTableRow = {
  project: string;
  name: string;
  price: Decimal;
  date: string;
  orderId: string;
  playerId: string;
  publisherPurchaseId: string | undefined;
  playerEmail: string | undefined;
  transactionId: string;
  paymentFlow: EAwardFlowStatus;
  status: string | undefined;
  currencySymbol: string;
  id: string;
  publisherId: string;
  type: string;
  paymentMethod: PaymentMethod;
};

export const createOrderTableRow = (
  order: OrderDetails,
  currencyCodeToCurrencyMap: Map<string, Currency>,
  userDetails?: UserDetails | null
): OrderTableRow => {
  const projects = userDetails?.projects;
  const projectName = projects?.find(
    (project: Project) => project.publisherId === order.publisherId
  )?.publisherName; // Get the currency multiplier
  const currency = currencyCodeToCurrencyMap.get(order.currency);
  const multiplier = currency?.multiplier || 1;

  const displayPrice = order.amountTotal.div(multiplier);

  return {
    project: projectName ?? '',
    name: order.bundleName,
    price: displayPrice,
    date: order.createdAt,
    orderId: order.orderId,
    playerId: order.playerId,
    publisherPurchaseId: order.publisherPurchaseId,
    transactionId: order.publisherPurchaseId ?? '',
    paymentFlow: order.awardFlow,
    status: order.state,
    id: order._id,
    type: order.offerSnapshot?.type ?? '',
    currencySymbol: order.currencySymbol,
    playerEmail: order.playerEmail,
    publisherId: order.publisherId,
    paymentMethod: order.paymentMethod
  };
};

export const getProjectsFromCache = (userDetails?: UserDetails | null) => {
  if (userDetails) {
    return userDetails.projects;
  }

  try {
    const queryClient = require('@tanstack/react-query').useQueryClient();
    const cachedData = queryClient?.getQueryData([EApiRoutes.USERS]);
    return cachedData?.projects;
  } catch {
    return undefined;
  }
};

export const getDefaultSelectedProjects = (
  allowedPublishers: string[],
  userDetails?: UserDetails | null
): string[] => {
  const projectsDetails =
    userDetails?.projects || getProjectsFromCache(userDetails);

  if (!projectsDetails) return [];

  return getSelectProjectOptions(projectsDetails, allowedPublishers).map(
    (project) => project.value
  );
};

export const cleanFilters = (filters: OrderFilters): OrderFilters => {
  const cleaned: any = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      // Strip dashes from receiptId before sending to API
      if (key === 'receiptId' && typeof value === 'string') {
        cleaned[key] = value.replace(/-/g, '');
      } else {
        cleaned[key] = value;
      }
    }
  });

  return cleaned;
};

export const updateQueryParam = (
  paramName: string,
  paramValue: string,
  location: any,
  navigate: any
) => {
  const queryParams = new URLSearchParams(location.search);

  if (paramValue) {
    queryParams.set(paramName, paramValue);
  } else {
    queryParams.delete(paramName);
  }

  const navigationOpt: { replace: boolean; state?: Record<string, boolean> } = {
    replace: true
  };

  if (location?.state) {
    navigationOpt.state = location.state;
  }

  navigate(`${location.pathname}?${queryParams.toString()}`, navigationOpt);
};

export const resetQueryParams = (
  paramsToReset: string[],
  location: any,
  navigate: any
) => {
  const newParams = new URLSearchParams(location.search);

  paramsToReset.forEach((param) => newParams.delete(param));

  const navigationOpt: { replace: boolean; state?: Record<string, boolean> } = {
    replace: true
  };

  if (location?.state) {
    navigationOpt.state = location.state;
  }

  navigate(`${location.pathname}?${newParams.toString()}`, navigationOpt);
};

export const filterStatusesByOrderType = (
  statuses: EOrderStatus[],
  isFree?: boolean
): EOrderStatus[] => {
  if (isFree) {
    const freeOrderStatuses = [
      EOrderStatus.created,
      EOrderStatus.charge_pending,
      EOrderStatus.charge_succeed,
      EOrderStatus.charge_failed
    ];
    return statuses.filter((status) => freeOrderStatuses.includes(status));
  }

  return statuses;
};

export const getStatusDisplayText = (
  status: EOrderStatus,
  isFree?: boolean
): string => {
  if (isFree) {
    switch (status) {
      case EOrderStatus.charge_pending:
        return 'Award Pending';
      case EOrderStatus.charge_succeed:
        return 'Completed Successfully';
      case EOrderStatus.charge_failed:
        return 'Failed';
      default:
        break;
    }
  }

  return statusDictionary[status]?.text || status;
};

export const groupStatusesByText = (
  statusesWithText: Array<{ text: string; status: EOrderStatus }>
): StatusOption[] => {
  const grouped = statusesWithText.reduce(
    (acc, { text, status }) => {
      if (!acc[text]) {
        acc[text] = { text, statuses: [] };
      }
      acc[text].statuses.push(status);
      return acc;
    },
    {} as Record<string, { text: string; statuses: EOrderStatus[] }>
  );

  return Object.values(grouped).map((item) => ({
    text: item.text,
    statuses: item.statuses.join(ordersUtils.STATUSES_SEPARATOR)
  }));
};

export const sortStatusOptionsAlphabetically = (
  options: Array<{ text: string; status: EOrderStatus }>
): Array<{ text: string; status: EOrderStatus }> => {
  return options.sort((a, b) => {
    const textA = a.text.toLowerCase();
    const textB = b.text.toLowerCase();
    return textA.localeCompare(textB);
  });
};

export const createStatusFilterOptions = (
  validStatuses: EOrderStatus[],
  isFree?: boolean
): StatusOption[] => {
  const filteredStatuses = filterStatusesByOrderType(validStatuses, isFree);

  const statusesWithText = filteredStatuses.map((status) => ({
    text: getStatusDisplayText(status, isFree),
    status
  }));

  const sortedStatuses = sortStatusOptionsAlphabetically(statusesWithText);
  const groupedOptions = groupStatusesByText(sortedStatuses);

  return groupedOptions;
};

export const getTableSortingState = (sorting: {
  sortValue: SortingOrderValue;
  direction: SortingDirection;
}): SortingState => {
  return [
    {
      id: 'date',
      desc: sorting.direction === SortingDirection.DESC
    }
  ];
};
